import type { AthletesParams } from "@/api/athletes/types";
import type { StaffMembersParams } from "@/api/staff-members/types";
import type { GetEventsParams } from "@/api/events/types";
import type { EventNotificationsParams } from "@/api/notifications/types";
import type { ExercisesParams } from "@/api/exercises/types";

export const queryKeys = {
  disciplines: {
    all: ["disciplines"] as const,
    detail: (id: string) => [...queryKeys.disciplines.all, id] as const,
    byGym: (gymId: number, status?: string) =>
      [
        ...queryKeys.disciplines.all,
        "gym",
        gymId,
        ...(status ? [status] : []),
      ] as const,
    byGymId: (gymId: number | undefined) =>
      [...queryKeys.disciplines.all, "gymId", gymId] as const,
  },

  gymExercises: {
    all: ["gymExercises"] as const,
    byGym: (gymId: number, status?: string) =>
      [
        ...queryKeys.gymExercises.all,
        "gym",
        gymId,
        ...(status ? [status] : []),
      ] as const,
  },

  athletes: {
    all: ["athletes"] as const,
    list: (params?: AthletesParams) =>
      [...queryKeys.athletes.all, params] as const,
    detail: (id: number) => [...queryKeys.athletes.all, id] as const,
  },

  staff: {
    all: ["staff-members"] as const,
    list: (params?: StaffMembersParams) =>
      [...queryKeys.staff.all, params] as const,
    detail: (id: number) => [...queryKeys.staff.all, id] as const,
  },

  events: {
    all: ["events"] as const,
    list: (params?: GetEventsParams) =>
      [...queryKeys.events.all, params] as const,
    detail: (eventId: number) => [...queryKeys.events.all, eventId] as const,
    availableAthletes: (
      eventId: number,
      params?: { search?: string; page?: number; perPage?: number },
    ) =>
      [
        ...queryKeys.events.detail(eventId),
        "availableAthletes",
        ...(params ? [params] : []),
      ] as const,
    attendances: (eventId: number, params?: { search?: string }) =>
      [
        ...queryKeys.events.detail(eventId),
        "attendances",
        ...(params ? [params] : []),
      ] as const,
  },

  gymSchedules: {
    all: ["gymSchedules"] as const,
    list: (gymId: number, params?: object) =>
      [
        ...queryKeys.gymSchedules.all,
        gymId,
        "list",
        ...(params ? [params] : []),
      ] as const,
    detail: (gymId: number, scheduleId: number) =>
      [...queryKeys.gymSchedules.all, gymId, scheduleId] as const,
  },

  notifications: {
    all: ["notifications"] as const,
    list: (gymId: number, eventId: number, params?: EventNotificationsParams) =>
      [
        ...queryKeys.notifications.all,
        gymId,
        eventId,
        ...(params ? [params] : []),
      ] as const,
  },

  gyms: {
    all: ["gyms"] as const,
  },

  rooms: {
    all: ["gymRooms"] as const,
    list: (gymId: number) => [...queryKeys.rooms.all, gymId] as const,
    detail: (id: string) => [...queryKeys.rooms.all, id] as const,
  },

  roles: {
    all: ["roles"] as const,
  },

  exercises: {
    all: ["exercises"] as const,
    list: (params?: ExercisesParams) =>
      [...queryKeys.exercises.all, params] as const,
    detail: (id: string) => [...queryKeys.exercises.all, id] as const,
    infinite: (params?: Omit<ExercisesParams, "page" | "perPage">) =>
      [...queryKeys.exercises.all, "infinite", params] as const,
  },

  gymScheduleBlueprints: {
    all: ["gymScheduleBlueprints"] as const,
    detail: (gymId: number, scheduleId: number) =>
      [...queryKeys.gymScheduleBlueprints.all, gymId, scheduleId] as const,
  },

  eventBlueprints: {
    all: ["eventBlueprints"] as const,
    list: (gymId: number, blueprintId: number, staffMemberId: number) =>
      [
        ...queryKeys.eventBlueprints.all,
        gymId,
        blueprintId,
        "list",
        staffMemberId,
      ] as const,
    detail: (gymId: number, blueprintId: number, eventBlueprintId: number) =>
      [
        ...queryKeys.eventBlueprints.all,
        gymId,
        blueprintId,
        eventBlueprintId,
      ] as const,
  },

  personalSchedules: {
    all: ["personalSchedules"] as const,
    staff: (staffMemberId: number, params?: object) =>
      [
        ...queryKeys.personalSchedules.all,
        "staff",
        staffMemberId,
        ...(params ? [params] : []),
      ] as const,
  },

  workoutBlocks: {
    all: ["workoutBlocks"] as const,
    list: (athleteId: number) =>
      [...queryKeys.workoutBlocks.all, athleteId, "list"] as const,
    detail: (athleteId: number, workoutBlockId: number) =>
      [...queryKeys.workoutBlocks.all, athleteId, workoutBlockId] as const,
  },

  physicalEvaluationSessions: {
    all: ["physicalEvaluationSessions"] as const,
    list: (athleteId: number) =>
      [...queryKeys.physicalEvaluationSessions.all, athleteId, "list"] as const,
  },

  workoutPlans: {
    all: ["workoutPlans"] as const,
    list: (workoutBlockId: number) =>
      [...queryKeys.workoutPlans.all, workoutBlockId, "list"] as const,
    detail: (workoutPlanId: number) =>
      [...queryKeys.workoutPlans.all, workoutPlanId] as const,
  },

  workoutBlockInstances: {
    all: ["workoutBlockInstances"] as const,
    list: (workoutBlockId: number) =>
      [...queryKeys.workoutBlockInstances.all, workoutBlockId, "list"] as const,
  },

  athleteObjectives: {
    all: ["athleteObjectives"] as const,
    list: () => [...queryKeys.athleteObjectives.all, "list"] as const,
  },

  auth: {
    verifyToken: (token: string) => ["verifyResetToken", token] as const,
  },
} as const;
